// Security.js - A segurança da própria conta
//
// O MFA é obrigatório, por isso aqui não há "desligar": só se vê o estado
// e se geram códigos de recuperação novos. Quem perder o telemóvel E os
// códigos pede a um administrador que reponha o MFA.
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, KeyRound, AlertCircle, Copy, Download } from 'lucide-react';
import ViewHeader from './ViewHeader';
import '../MfaStep.css';

const API_URL = '/api';

function authHeaders(extra = {}) {
  const token = localStorage.getItem('access_token');
  return { ...extra, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

// Abaixo disto avisa-se: quem gastar os códigos todos e perder o telemóvel
// fica trancado até um administrador intervir.
const POUCOS_CODIGOS = 3;

function Security({ onBack }) {
  const { t, i18n } = useTranslation();
  const [estado, setEstado] = useState(null);
  const [erro, setErro] = useState('');
  const [codigo, setCodigo] = useState('');
  const [aGerar, setAGerar] = useState(false);
  const [novos, setNovos] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/auth/mfa/status`, { headers: authHeaders() });
      if (r.ok) setEstado(await r.json());
    } catch {
      setErro(t('seguranca.erroEstado'));
    }
  }, [t]);

  useEffect(() => { carregar(); }, [carregar]);

  const gerar = async () => {
    if (!codigo.trim()) return;
    setAGerar(true);
    setErro('');
    try {
      const r = await fetch(`${API_URL}/auth/mfa/recovery-codes`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ code: codigo }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || t('seguranca.erroGerar'));
      setNovos(d.recovery_codes);
      setCodigo('');
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setAGerar(false);
    }
  };

  const texto = () => `${t('mfa.ficheiroTitulo')}\n\n${(novos || []).join('\n')}\n\n${t('mfa.ficheiroNota')}\n`;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto());
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch { /* fica o descarregar */ }
  };

  const descarregar = () => {
    const url = URL.createObjectURL(new Blob([texto()], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'smart-home-codigos-recuperacao.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const data = estado?.enrolled_at
    ? new Date(estado.enrolled_at).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'pt-PT')
    : '—';

  return (
    <div className="app-container">
      <div className="app-content">
        <ViewHeader
          icon={ShieldCheck}
          iconClass="icon-heatpump"
          titulo={t('seguranca.titulo')}
          subtitulo={t('seguranca.subtitulo')}
          onBack={onBack}
        />

        {erro && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{erro}</span>
          </div>
        )}

        <div className="heatpump-container">
          <div className={`heatpump-status-card ${estado?.enabled ? 'online' : ''}`}>
            <div className="heatpump-status-header">
              <div className="status-indicator">
                <div className={`status-dot ${estado?.enabled ? 'online' : 'offline'}`} />
                <span>{t('seguranca.verificacaoDoisPassos')}</span>
              </div>
              <div className={`power-badge ${estado?.enabled ? 'on' : 'off'}`}>
                {estado?.enabled ? t('seguranca.ativa') : t('seguranca.inativa')}
              </div>
            </div>
          </div>

          <div className="heatpump-info-card">
            <h3>{t('comum.informacoes')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <ShieldCheck size={20} />
                <div>
                  <span className="info-label">{t('seguranca.ativadaEm')}</span>
                  <span className="info-value">{data}</span>
                </div>
              </div>
              <div className="info-item">
                <KeyRound size={20} />
                <div>
                  <span className="info-label">{t('seguranca.codigosRestantes')}</span>
                  <span className="info-value">{estado ? estado.recovery_remaining : '—'}</span>
                </div>
              </div>
            </div>
            {estado && estado.recovery_remaining <= POUCOS_CODIGOS && (
              <p className="section-help" style={{ marginTop: '1rem' }}>
                {t('seguranca.poucosCodigos')}
              </p>
            )}
          </div>

          <div className="heatpump-controls-card">
            <h3>{t('seguranca.novosCodigosTitulo')}</h3>

            {novos ? (
              <>
                <p className="mfa-texto">{t('mfa.recuperacaoTexto')}</p>
                <div className="mfa-codigos">
                  {novos.map((c) => <code key={c}>{c}</code>)}
                </div>
                <div className="mfa-acoes">
                  <button type="button" className="mfa-btn-sec" onClick={copiar}>
                    <Copy size={16} /> {copiado ? t('mfa.copiado') : t('mfa.copiar')}
                  </button>
                  <button type="button" className="mfa-btn-sec" onClick={descarregar}>
                    <Download size={16} /> {t('mfa.descarregar')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mfa-texto">{t('seguranca.novosCodigosTexto')}</p>
                <div className="form-group">
                  <input
                    className="mfa-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={7}
                    placeholder="000000"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && gerar()}
                  />
                </div>
                <button className="login-button" onClick={gerar} disabled={aGerar || !codigo.trim()}>
                  <KeyRound size={18} /> {aGerar ? t('mfa.aVerificar') : t('seguranca.gerar')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Security;
